/**
 * HTTP Client configuration options
 */
export interface HttpClientConfig {
  baseUrl: string;
  getToken?: () => Promise<string> | string;
  headers?: Record<string, string>;
}

/**
 * HTTP Request options
 */
export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * HTTP Error response
 */
export interface HttpErrorResponse {
  error?: {
    message?: string;
    code?: string;
    details?: unknown;
  };
}

/**
 * HTTP Client Error class
 */
export class HttpClientError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string,
    public details?: unknown,
    public rawBody?: string
  ) {
    super(message);
    this.name = 'HttpClientError';
  }
}

/**
 * Reusable HTTP Client for making API requests
 */
export class HttpClient {
  private baseUrl: string;
  private getToken?: () => Promise<string> | string;
  private defaultHeaders: Record<string, string>;

  constructor(config: HttpClientConfig) {
    this.baseUrl = config.baseUrl;
    this.getToken = config.getToken;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers
    };
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    const url = `${this.baseUrl}${path}`;

    if (!params) {
      return url;
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  /**
   * Get headers with authentication token
   */
  private async getHeaders(
    customHeaders?: Record<string, string>
  ): Promise<Record<string, string>> {
    const headers = {...this.defaultHeaders, ...customHeaders};

    if (this.getToken) {
      const token = await this.getToken();
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle HTTP response
   * Handles 204 No Content and other empty responses
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const rawBody = await response.text().catch(() => '');
      let error: HttpErrorResponse = {};
      try {
        error = JSON.parse(rawBody) as HttpErrorResponse;
      } catch {
        // non-JSON body — rawBody preserved as-is
      }

      throw new HttpClientError(
        error.error?.message ?? (error as {message?: string}).message ?? response.statusText,
        response.status,
        error.error?.code ?? (error as {type?: string}).type,
        error.error?.details,
        rawBody
      );
    }

    // Handle 204 No Content and similar responses with no body
    if (
      response.status === 204 ||
      response.status === 205 ||
      response.headers.get('content-length') === '0'
    ) {
      return undefined as T;
    }

    // Try to parse as JSON; fall back to raw text for non-JSON responses (e.g. Slack's plain "ok").
    // Callers that expect JSON should only hit the fallback if the server sends a non-JSON body,
    // which is a server-side issue and will surface as a downstream runtime error.
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  /**
   * GET request
   */
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, options?.params);
    const headers = await this.getHeaders(options?.headers);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    return this.handleResponse<T>(response);
  }

  /**
   * POST request
   */
  async post<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, options?.params);
    const headers = await this.getHeaders(options?.headers);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PATCH request
   */
  async patch<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, options?.params);
    const headers = await this.getHeaders(options?.headers);

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PUT request
   */
  async put<T, D = unknown>(path: string, data?: D, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, options?.params);
    const headers = await this.getHeaders(options?.headers);

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  /**
   * DELETE request
   */
  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, options?.params);
    const headers = await this.getHeaders(options?.headers);

    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });

    return this.handleResponse<T>(response);
  }
}
